import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
} from "react";

interface ProfileBalance {
  data: any
}

interface ProfileContextType {
  loading: boolean;
  balance: any;
  error: string | null;
  refreshBalance: () => Promise<void>;
  setBalance?: any;
}

interface Props {
  children: ReactNode;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

const apiUrl = process.env.REACT_APP_API_URL;

export const ProfileBalanceProvider = ({ children }: Props) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [balance, setBalance] = useState<any>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();

      const response = await axios.get<any>(
        `${apiUrl}/api/v1/profile/balance`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setBalance( response.data.response.response);
    } catch (err: any) {
      setError(err.message || "Error al obtener el balance");
      setBalance(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Derivar datos calculados (opcional)
  const balanceFormatted = useMemo(() => {
    if (!balance) return null;
    return {
      totalTransactions: balance[0]?.total_transccion || 0,
      totalCost: balance[0]?.costo_total_sin_comision || 0,
    };
  }, [balance]);

  return (
    <ProfileContext.Provider
      value={{
        balance: balanceFormatted ?? balance,
        loading,
        error,
        refreshBalance: fetchBalance,
        setBalance,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

// Hook personalizado para acceder al contexto
export const useProfileBalance = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error(
      "useProfileBalance debe usarse dentro de un ProfileBalanceProvider"
    );
  }
  return context;
};
